/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DataCollectorService } from '../data_collector_services/data_collector_service.service';
import { UtilityService } from '../utility_functions/utility_functions.service';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class ContractsService {
  get contractsBaseURL(): any{
    return `${this.datacollectorService.baseurl()}/service_contract`;
  }
  get templateUrl(): any{
    return `${this.contractsBaseURL}/template`;
  }
  get contractsApiUrls(): any{
    return {
      dataUrl: `${this.datacollectorService.baseurl()}/service_contract/filter`,
      columnUrl: `${this.datacollectorService.baseurl()}/service_contract/meta`,
      filterUrl: `${this.datacollectorService.baseurl()}/service_contract/meta/filter`,
      bulkActionUrl: `${this.datacollectorService.baseurl()}/service_contract/bulk_action`,
      estimateUrl: `${this.datacollectorService.baseurl()}/estimate`,
      invoiceUrl: `${this.datacollectorService.baseurl()}/invoice`,
      noteAttachmentUrl: `${this.datacollectorService.baseurl()}/note_attachment`,
      contractTemplateUrl: `${this.datacollectorService.baseurl()}/service_contract/template`,
      billingPeriod: `${this.datacollectorService.baseurl()}/billing_period`,
      packageUrl: `${this.contractsBaseURL}/package`,
      projectUrl: `${this.datacollectorService.baseurl()}/projects`,
      ppmUrl: `${this.datacollectorService.baseurl()}/ppm`,
    };
  }
  contractDetailsLoaded = new Subject<boolean>();
  invoiceDates: any;
  dateFormat = this.utilityService.dateFormat;
  currencyDecimal: number = this.utilityService.getCurrencyDecimal();
  currentContractDetails = new BehaviorSubject(null);
  constructor(
    private datacollectorService: DataCollectorService,
    private utilityService: UtilityService
  ) {
    this.contractDetailsLoaded.next(false);
  }

  changeActivationStatus(
    contractUid: string,
    isActive: boolean
  ): Observable<any> {
    const contractsURL = `${this.contractsBaseURL}/${contractUid}/activate?is_active=${isActive}`;
    return this.datacollectorService.putData(contractsURL, {});
  }

  deactivateContract(contractUid: string, body: any): Observable<any> {
    const contractsURL = `${this.contractsBaseURL}/${contractUid}/activate`;
    return this.datacollectorService.putData(contractsURL, body);
  } 

  deleteContract(contractUid: string): any {
    const contractsURL = `${this.contractsBaseURL}/${contractUid}`;
    return this.datacollectorService.deleteData(contractsURL);
  }

  getContractDetails(contractId: string): string {
    return `${this.contractsBaseURL}/${contractId}`;
  }

  getContractNotes(contractUid: string): string {
    return `${this.datacollectorService.baseurl()}/notes`;
  }

  updateContractStatus(contractId: string, data: any): Observable<any> {
    const contractsURL = `${this.contractsBaseURL}/${contractId}/status?approval_status=${data.approval_status}&remarks=${data.remarks}`;
    return this.datacollectorService.putData(contractsURL, {});
  }
  updateContract(contractId: string, data: any): Observable<any> {
    const payload = { service_contract: data };
    const contractsUrl = `${this.contractsBaseURL}/${contractId}`;
    return this.datacollectorService.putData(contractsUrl, payload);
  }

  getPackageDetailUrl(uid: any): string{
    return `${this.contractsApiUrls?.packageUrl}/${uid}`;
  }

  getPrintSharePdfUrl(contractId: string): string{
    return `${this.getContractDetails(contractId)}/card`;
  }

  sendContract(contractId,sendData): Observable<any>{
    return this.datacollectorService.postData(this.getPrintSharePdfUrl(contractId),sendData);
  }

  generateInvoiceFields(options: any): any {
    this.invoiceDates = [];
    this.invoiceDates[0] = {};
    this.invoiceDates[0]['is_paid'] = false;
    this.invoiceDates[0]['billing_date'] = moment(options?.startDate, this.dateFormat);
    this.invoiceDates[0]['due_date'] = moment(
      options.startDate,
      this.dateFormat
    ).add(options?.paymentTerm?.no_of_days, 'days');
    this.invoiceDates[0]['invoice_date'] = moment(
      this.invoiceDates[0]['billing_date'],
      this.dateFormat
    ).subtract(options?.generateInvoiceBefore, 'days');
    let invoiceTerm = 0;
    const contractTotal = options.paymentTotal;
    if (options?.billingPeriod?.billing_period_type === 'DAYS') {
      invoiceTerm =
        moment(options?.expiryDate, this.dateFormat).diff(
          moment(options?.startDate, this.dateFormat),
          'days'
        ) / options?.billingPeriod?.billing_period_value;
    } else if (options?.billingPeriod?.billing_period_type === 'MONTHS') {
      invoiceTerm =
        options?.contractTerm / options?.billingPeriod?.billing_period_value;
    } else if (options?.billingPeriod?.billing_period_type === 'WEEKS') {
      invoiceTerm =
        moment(options?.expiryDate, this.dateFormat).diff(
          moment(options?.startDate, this.dateFormat),
          'days'
        ) /
        (options.billingPeriod.billing_period_value * 7);
    }

    if(options?.existingInvoiceTerm > 0){
      invoiceTerm = invoiceTerm - options?.existingInvoiceTerm;
    }
    for (let i = 1; i < invoiceTerm; i++) {
      this.invoiceDates[i] = {};
      if (options?.billingPeriod?.billing_period_type === 'DAYS') {
        this.invoiceDates[i]['billing_date'] = moment(
          this.invoiceDates[i - 1]['billing_date'],
          this.dateFormat
        ).add(options?.billingPeriod?.billing_period_value, 'days');
      } else if (options?.billingPeriod?.billing_period_type === 'MONTHS') {
        this.invoiceDates[i]['billing_date'] = moment(
          this.invoiceDates[i - 1]['billing_date'],
          this.dateFormat
        ).add(options?.billingPeriod?.billing_period_value, 'months');
      } else {
        this.invoiceDates[i]['billing_date'] = moment(
          this.invoiceDates[i - 1]['billing_date'],
          this.dateFormat
        ).add(options?.billingPeriod?.billing_period_value * 7, 'days');
      }
      this.invoiceDates[i]['is_paid'] = false;
      this.invoiceDates[i]['due_date'] = moment(
        this.invoiceDates[i]['billing_date'],
        this.dateFormat
      ).add(options?.paymentTerm?.no_of_days, 'days');
      this.invoiceDates[i]['invoice_date'] = moment(
        this.invoiceDates[i]['billing_date'],
        this.dateFormat
      ).subtract(options?.generateInvoiceBefore, 'days');
    }
    this.invoiceDates.map((dates, i) => {
      dates['total_amount'] = (
        contractTotal / this.invoiceDates.length
      ).toFixed(this.currencyDecimal);
    });
    
    const paymentHistory = [];
    const invoiceDates = [...this.invoiceDates];
    invoiceDates.forEach((dates, i) => {
      dates.billing_date = moment(dates?.billing_date, this.dateFormat)
        .startOf('day')
        .utc()
        .format('YYYY-MM-DD HH:mm:ss');
      dates.due_date = moment(dates?.due_date, this.dateFormat)
        .endOf('day')
        .utc()
        .format('YYYY-MM-DD HH:mm:ss');
      dates.invoice_date = moment(dates?.invoice_date, this.dateFormat)
        .startOf('day')
        .utc()
        .format('YYYY-MM-DD HH:mm:ss');
      paymentHistory.push(dates);
    });
    return paymentHistory;
  }

  getLineItems(lineItems): any{
    const data = [];
    let count = 1;
    lineItems.forEach((element: any, index)=>{
      if((['PART','PARTS','SERVICE','PRODUCT','SERVICES','PRODUCTS','BUNDLE'].includes(element?.product_type || element?.product_ref_id?.product_type) ||
          element?.line_item_type === 'ITEM')){
        data.push({...element,product_name: element?.product_name || element?.name,name: element?.product_name || element?.name,index: count});
        count += 1;
      }else{
        data.push({product_name: element?.product_name || element?.name, name: element?.product_name || element?.name, line_item_type: element?.line_item_type});
      }
    });
        return data;
  }
}
